import { APIResponse } from "@/api/@types";
import { InterceptorManager } from "@/api/httpsClient";
import { toastMessage } from "@/ui/components/Toast";
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";

interface RetryableRequest extends InternalAxiosRequestConfig {
  /** 네트워크/서버 오류 재시도 여부 */
  _retry?: boolean;
  /** 인증 리프레시 시도 여부 (네트워크 재시도와 분리해야 순단 후 401에서도 재발급 가능) */
  _authRetried?: boolean;
}

export type RefreshFailureReason =
  | "missing-refresh-token"
  | "temporary-failure"
  | "auth-rejected"
  | "invalid-response";

export interface RefreshTokenResult {
  token: string | null;
  reason?: RefreshFailureReason;
}

interface RefreshSubscriber {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}

// 토큰 갱신 관련 상태
let isRefreshing = false;
let refreshSubscribers: RefreshSubscriber[] = [];

// axios 인스턴스를 직접 받아서 사용
let axiosInstance: AxiosInstance | null = null;

// Axios 인스턴스 설정
export const setAxiosInstance = (instance: AxiosInstance): void => {
  axiosInstance = instance;
};

// 헬퍼 함수들
const shouldRetry = (error: AxiosError): boolean => {
  const status = error.response?.status;
  return !status || (status >= 500 && status <= 599);
};

const isTimeoutError = (error: AxiosError): boolean => {
  return error.code === "ECONNABORTED" || error.message.includes("timeout");
};

const isNetworkError = (error: AxiosError): boolean => {
  return (
    !error.response ||
    error.code === "NETWORK_ERROR" ||
    error.message.includes("Network Error")
  );
};

const delay = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

const getToken = (): string | null => {
  return localStorage.getItem("token");
};

const isB2cRuntime = (): boolean => {
  const isB2cRoute =
    typeof window !== "undefined" && window.location.pathname.startsWith("/b2c");
  const isB2cBuild = import.meta.env?.VITE_APP_TARGET === "b2c";
  return isB2cRoute || isB2cBuild;
};

const isAuthRefreshRequest = (url?: string): boolean => {
  return Boolean(url?.includes("/auth/refresh"));
};

const isAuthRejectedStatus = (status?: number): boolean => {
  return status === 401 || status === 403;
};

const shouldHandleAuthError = (status?: number): boolean => {
  return isB2cRuntime() ? status === 401 : isAuthRejectedStatus(status);
};

const shouldLogoutAfterRefreshFailure = (
  result: RefreshTokenResult,
): boolean => {
  if (!isB2cRuntime()) return true;

  return (
    result.reason === "auth-rejected" ||
    result.reason === "missing-refresh-token"
  );
};

const shouldLogoutAfterRetriedAuthError = (status?: number): boolean => {
  return isB2cRuntime() ? status === 401 : isAuthRejectedStatus(status);
};

const resolveRefreshSubscribers = (token: string): void => {
  refreshSubscribers.forEach(({ resolve }) => resolve(token));
  refreshSubscribers = [];
};

const rejectRefreshSubscribers = (error: unknown): void => {
  refreshSubscribers.forEach(({ reject }) => reject(error));
  refreshSubscribers = [];
};

export const refreshTokenAsync = async (): Promise<RefreshTokenResult> => {
  const previousRefreshToken = localStorage.getItem("refreshToken");
  if (!previousRefreshToken || !axiosInstance) {
    return { token: null, reason: "missing-refresh-token" };
  }

  try {
    const headers: Record<string, string> = {
      "Refresh-Token": `Bearer ${previousRefreshToken}`,
    };

    const response = await axios.post(
      "/api/v1/auth/refresh",
      {},
      {
        baseURL: axiosInstance.defaults.baseURL,
        headers,
        timeout: 10000, // 토큰 갱신 요청에도 타임아웃 설정
      },
    );

    // @ts-ignore
    const token = response?.data?.data?.accessToken;
    // @ts-ignore
    const refreshToken = response?.data?.data?.refreshToken;

    if (token) {
      localStorage.setItem("token", token);
      if (refreshToken) {
        localStorage.setItem("refreshToken", refreshToken);
      }
      return { token };
    }
    return { token: null, reason: "invalid-response" };
  } catch (error) {
    const status = (error as AxiosError).response?.status;
    const reason = isAuthRejectedStatus(status)
      ? "auth-rejected"
      : "temporary-failure";
    console.error("refreshToken Error", error);
    return { token: null, reason };
  }
};

const handleLogout = (): void => {
  const isB2c = window.location.pathname.startsWith("/b2c") || isB2cRuntime();

  if (isB2c) {
    localStorage.removeItem("token");
    window.location.href = "/b2c/auth";
    return;
  }

  localStorage.removeItem("token");
  localStorage.removeItem("refreshToken");
  // Zustand persist 저장소도 함께 삭제하여 로그인 페이지로 정상 이동되도록 함
  localStorage.removeItem("tailody-user-storage");
  localStorage.removeItem("tailody-group-storage");
  window.location.href = "/login";
};

const showNetworkErrorToast = (): void => {
  if (toastMessage) {
    toastMessage({
      message: "네트워크 오류가 있습니다. 새로고침을 진행해주세요.",
      type: "error",
    });
  }
};

const showTimeoutErrorToast = (): void => {
  if (toastMessage) {
    toastMessage({
      message: `요청 시간이 초과되었습니다. \n다시 시도해주세요.`,
      type: "error",
    });
  }
};

// HLS/스트리밍 관련 URL 패턴들
const HLS_RELATED_PATTERNS = [
  // "/streaming",
  // "/hls",
  // "/live",
  "/broadcast",
  "/m3u8",
  ".m3u8",
  ".ts",
];

// URL이 HLS/스트리밍 관련인지 확인하는 함수
const isHlsRelatedRequest = (url?: string): boolean => {
  if (!url) return false;
  return HLS_RELATED_PATTERNS.some((pattern) =>
    url.toLowerCase().includes(pattern.toLowerCase()),
  );
};

export const setupWebManageInterceptors = (): InterceptorManager => {
  return {
    request: {
      onFulfilled: (config: InternalAxiosRequestConfig) => {
        const token = getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // HLS 관련 요청이 아닌 경우에만 기본 타임아웃 설정
        if (!config.timeout && !isHlsRelatedRequest(config.url)) {
          config.timeout = 30000; // 30초 기본 타임아웃
        }

        // HLS 관련 요청인 경우 타임아웃 제거 또는 매우 큰 값으로 설정
        if (isHlsRelatedRequest(config.url)) {
          config.timeout = 0; // 타임아웃 비활성화
          // 또는 config.timeout = 300000; // 5분 등 긴 시간으로 설정
        }

        return config;
      },
      onRejected: (error: unknown) => Promise.reject(error),
    },
    response: {
      onFulfilled: (response: AxiosResponse<APIResponse<unknown>>) => {
        // @ts-ignore
        if (response?.data?.data?.tokenInfo) {
          // @ts-ignore
          const token = response?.data?.data?.tokenInfo.accessToken;
          // @ts-ignore
          const refreshToken = response?.data?.data?.tokenInfo.refreshToken;

          if (token) {
            localStorage.setItem("token", token);
            if (refreshToken) {
              localStorage.setItem("refreshToken", refreshToken);
            }
          }
        }
        return response;
      },
      onRejected: async (error: AxiosError): Promise<unknown> => {
        const originalRequest = error.config as RetryableRequest;
        if (!originalRequest) {
          throw error;
        }

        // 타임아웃 에러 처리 - 토스트만 표시하고 재시도 없음
        if (isTimeoutError(error)) {
          // refresh 토큰 요청이나 HLS 관련 요청이 아닌 경우에만 토스트 메시지 표시
          if (
            !isAuthRefreshRequest(originalRequest.url) &&
            !isHlsRelatedRequest(originalRequest.url)
          ) {
            showTimeoutErrorToast();
          }
          throw error; // 재시도 없이 에러 throw
        }

        // 네트워크 오류 처리 (타임아웃 제외)
        if (isNetworkError(error)) {
          // refresh 토큰 요청이나 HLS 관련 요청이 아닌 경우에만 토스트 메시지 표시
          if (
            !isAuthRefreshRequest(originalRequest.url) &&
            !isHlsRelatedRequest(originalRequest.url)
          ) {
            showNetworkErrorToast();
          }

          // 네트워크 오류의 경우 재시도 로직 적용
          if (!originalRequest._retry) {
            let retryCount = 0;
            originalRequest._retry = true;
            const config = { retryCount: 3 };

            while (retryCount < config.retryCount) {
              try {
                await delay(retryCount + 1);

                if (!axiosInstance) {
                  throw new Error("Axios instance not set");
                }

                return axiosInstance.request(originalRequest);
              } catch (retryError) {
                retryCount++;
                if (retryCount === config.retryCount) {
                  // 마지막 재시도도 실패한 경우 네트워크 오류 토스트 다시 표시
                  if (isNetworkError(retryError as AxiosError)) {
                    showNetworkErrorToast();
                  }
                  throw retryError;
                }
              }
            }
          }

          throw error;
        }

        console.error("[originalRequest._retry]", originalRequest._retry);

        // 인증 에러 및 토큰 갱신 처리
        // 네트워크 재시도(_retry)와 무관하게, 아직 리프레시를 시도하지 않았으면(_authRetried)
        // 재발급을 시도한다. (기존: _retry 공유로 순단 재시도 후 401이면 재발급 없이 로그아웃)
        if (
          shouldHandleAuthError(error.response?.status) &&
          !originalRequest._authRetried
        ) {
          // refresh 요청 자체가 실패한 경우
          if (isAuthRefreshRequest(originalRequest.url)) {
            handleLogout();
            throw error;
          }

          // 이미 토큰 갱신 중인 경우
          if (isRefreshing) {
            try {
              const token = await new Promise<string>((resolve, reject) => {
                refreshSubscribers.push({ resolve, reject });
              });
              originalRequest._authRetried = true;
              originalRequest.headers.Authorization = `Bearer ${token}`;

              // axios 인스턴스가 있으면 사용
              if (!axiosInstance) {
                throw new Error("Axios instance not set");
              }

              return axiosInstance.request(originalRequest);
            } catch (err) {
              return Promise.reject(err);
            }
          }

          // 토큰 갱신 시작
          originalRequest._authRetried = true;
          isRefreshing = true;

          let logoutHandled = false;
          try {
            const refreshResult = await refreshTokenAsync();
            const newToken = refreshResult.token;

            if (newToken) {
              isRefreshing = false;
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              // 대기 중인 요청들에 새 토큰 전달
              resolveRefreshSubscribers(newToken);

              if (!axiosInstance) {
                throw new Error("Axios instance not set");
              }

              return axiosInstance.request(originalRequest);
            }
            isRefreshing = false;
            rejectRefreshSubscribers(error);
            if (shouldLogoutAfterRefreshFailure(refreshResult)) {
              handleLogout();
              logoutHandled = true;
            }
            throw error;
          } catch (refreshError) {
            if (!logoutHandled && !isB2cRuntime()) {
              handleLogout();
            }
            isRefreshing = false;
            rejectRefreshSubscribers(refreshError);
            throw refreshError;
          }
        } else if (
          shouldLogoutAfterRetriedAuthError(error.response?.status) &&
          originalRequest._authRetried
        ) {
          // 리프레시까지 했는데도 401 → 실제 인증 실패 → 로그아웃
          handleLogout();
        }

        // 서버 에러에 대한 재시도 로직 (네트워크 오류가 아닌 경우)
        if (shouldRetry(error) && !originalRequest._retry) {
          let retryCount = 0;
          originalRequest._retry = true;
          const config = { retryCount: 3, retryDelay: 1000 };

          while (retryCount < config.retryCount) {
            try {
              await delay(config.retryDelay * retryCount);

              if (!axiosInstance) {
                throw new Error("Axios instance not set");
              }

              return axiosInstance.request(originalRequest);
            } catch (retryError) {
              retryCount++;
              if (retryCount === config.retryCount) {
                throw retryError;
              }
            }
          }
        }

        throw error;
      },
    },
  };
};
